import React, { useEffect, useState } from "react";
import { InlineLoading, Form, Modal, Select, SelectItem } from "carbon-components-react";

import { AppShape, ContactShape } from "@eave/shared/types/api";
import APIClient from "web_modules/APIClient";

const styles = require("./styles.module.scss");

/**
 *  API helper functions
 */
const fetchAppById = appID =>
  APIClient.get(`/loan_applications/${appID}`).then(({ application }) => {
    return application;
  }) as Promise<AppShape>;

type ContactOptionsResponse = {
  loan_managers: any[];
  loan_officers: any[];
  underwriters: any[];
};

// N.B pulling this dynamically, since LO options are based off app's state
const fetchContactOptions = appID =>
  APIClient.get(`/uw/${appID}/contacts/options`) as Promise<ContactOptionsResponse>;

const updateContact = (appID: string, contact: ContactShape) =>
  APIClient.post("/contacts", {
    ...contact,
    loan_application_id: +appID, // can't unmarshal a string as int64 on backend :((
    source: "ContactInfoHurdle", // HACK: we resolve contacts on the backend by sources, this is highest priority
  });

enum OperatorLabel {
  UW = "uw",
  LO = "lo",
  LM = "lm",
}

type Props = {
  appID: string;
  onClose: Function;
};

const UpdateOperatorModal = ({ appID, onClose }: Props) => {
  /**
   *  UI state
   */
  const [loaded, setLoaded] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(true);

  /**
   * Data-related state
   */
  const [optionsUW, setOptionsUW] = useState([]);
  const [optionsLO, setOptionsLO] = useState([]);
  const [optionsLM, setOptionsLM] = useState([]);

  const [selectedUW, setSelectedUW] = useState("");
  const [selectedLO, setSelectedLO] = useState("");
  const [selectedLM, setSelectedLM] = useState("");

  /**
   * Data fetching & updating
   */
  useEffect(() => {
    Promise.all([fetchAppById(appID), fetchContactOptions(appID)])
      .then(([appShape, contactOpts]) => {
        // Set default selected contacts
        const { contacts } = appShape;

        const { loan_manager, loan_officer, loan_underwriter } = contacts;
        const loanManagerName = loan_manager && loan_manager.name ? loan_manager.name : "";
        const loanOfficerName = loan_officer && loan_officer.name ? loan_officer.name : "";
        const underwriterName =
          loan_underwriter && loan_underwriter.name ? loan_underwriter.name : "";

        setSelectedLO(loanOfficerName);
        setSelectedLM(loanManagerName);
        setSelectedUW(underwriterName);

        // set options for each operator
        const { underwriters, loan_officers, loan_managers } = contactOpts;
        setOptionsUW(underwriters);
        setOptionsLO(loan_officers);
        setOptionsLM(loan_managers);

        setLoaded(true);
      })
      .finally(() => {});
  }, [appID]);

  const assignContact = () => {
    const uwContact = optionsUW.find(uw => uw.name === selectedUW);
    const lmContact = optionsLM.find(lm => lm.name === selectedLM);
    const loContact = optionsLO.find(lo => lo.name === selectedLO);

    Promise.all([
      updateContact(appID, uwContact),
      updateContact(appID, lmContact),
      updateContact(appID, loContact),
    ]).then(() => {
      setModalIsOpen(false);
      onClose();
    });
  };

  const handleOnChange = e => {
    const {
      target: { name, value },
    } = e;

    switch (name) {
      case OperatorLabel.UW:
        setSelectedUW(value);
        break;
      case OperatorLabel.LO:
        setSelectedLO(value);
        break;
      case OperatorLabel.LM:
        setSelectedLM(value);
        break;
      default:
        throw new Error(`no input handler for name: ${name}`);
    }
  };

  /**
   *  helper functions
   */
  const reset = () => {
    setModalIsOpen(false);
    onClose();
  };

  return (
    <Modal
      modalHeading="Assign operator to loan"
      primaryButtonText="Assign operator"
      secondaryButtonText="Cancel"
      size="sm"
      open={modalIsOpen}
      onBlur={reset}
      onRequestClose={reset}
      onRequestSubmit={assignContact}
      className={styles.modalContactsForm}
    >
      <Form>
        {/* Underwriter */}
        <Select
          labelText="underwriter"
          id={`underwriter-app-${appID}`}
          name={OperatorLabel.UW}
          value={selectedUW}
          onChange={handleOnChange}
          style={{ marginBottom: "10px" }}
        >
          <SelectItem value="" text="Select underwriter" />
          {optionsUW.map(({ name }) => (
            <SelectItem value={name} text={name} key={name} />
          ))}
        </Select>

        {/* loan managers */}
        <Select
          labelText="loan managers"
          id={`loan-manager-app-${appID}`}
          name={OperatorLabel.LM}
          value={selectedLM}
          onChange={handleOnChange}
          style={{ marginBottom: "10px" }}
        >
          <SelectItem value="" text="Select loan manager" />
          {optionsLM.map(({ name }) => (
            <SelectItem value={name} text={name} key={name} />
          ))}
        </Select>

        {/* loan officers */}
        <Select
          labelText="loan officer"
          id={`loan-officer-app-${appID}`}
          name={OperatorLabel.LO}
          value={selectedLO}
          onChange={handleOnChange}
          style={{ marginBottom: "10px" }}
        >
          <SelectItem value="" text="Select loan officer" />
          {optionsLO.map(({ name }) => (
            <SelectItem value={name} text={name} key={name} />
          ))}
        </Select>

        <InlineLoading
          description={loaded ? "Loaded" : "Loading..."}
          status={loaded ? "finished" : "active"}
        />
      </Form>
    </Modal>
  );
};

export default UpdateOperatorModal;
